/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: chen
 *  创建时间: 2026-03-05 10:07
 *  文件名: src/components/withHiddenLabel/index.tsx
 *  功能描述:
 */

import React from 'react';
import { connect } from 'react-redux';
import { IColumnItem, getSearchParams, isHiddenLabelColumn } from '../../util';

interface RootState {
  Menu: {
    menus: IColumnItem[];
  };
}

interface WithHiddenLabelState {
  hiddenLabel: boolean;
}

// 定义需要的外部 props
interface WithHiddenLabelProps {
  location?: Location | { search: string };
  menus?: IColumnItem[]; // 通过 connect 注入
}

// 定义注入的 props
interface InjectedHiddenLabelProps {
  hiddenLabel: boolean;
}

const withHiddenLabel = <P extends InjectedHiddenLabelProps>(
  WrappedComponent: React.ComponentType<P>,
  options?: {
    columnIdFieldName?: string; // 可配置的字段名，默认 'id'
  },
) => {
  // 合并配置，设置默认值
  const { columnIdFieldName = 'id' } = options || {};

  // 定义组件的 props 类型，排除已经注入的 props
  type Props = Omit<P, keyof InjectedHiddenLabelProps> & WithHiddenLabelProps;

  const mapStateToProps = (state: RootState) => ({
    menus: state.Menu.menus,
  });

  class WithHiddenLabel extends React.Component<
    Props & { menus: IColumnItem[] },
    WithHiddenLabelState
  > {
    constructor(props: Props & { menus: IColumnItem[] }) {
      super(props);
      this.state = {
        hiddenLabel: false,
      };
    }

    componentDidMount() {
      this.calculateHiddenLabel();
    }

    componentDidUpdate(prevProps: Props & { menus: IColumnItem[] }) {
      if (
        prevProps.location?.search !== this.props.location?.search ||
        prevProps.menus !== this.props.menus
      ) {
        this.calculateHiddenLabel();
      }
    }

    calculateHiddenLabel = (): void => {
      const searchParams = getSearchParams(this.props.location?.search || '');

      // 根据配置的字段名获取 id
      const id = searchParams[columnIdFieldName];

      // 可以添加调试日志（可选）
      if (process.env.NODE_ENV !== 'production') {
        console.log(`[withHiddenLabel] 使用字段名 "${columnIdFieldName}" 获取 id:`, id);
      }

      const hiddenLabel = isHiddenLabelColumn(id, this.props.menus);
      this.setState({ hiddenLabel });
    };

    render() {
      const { menus, ...restProps } = this.props;

      // 将 hiddenLabel 注入到 wrapped component
      return <WrappedComponent {...(restProps as P)} hiddenLabel={this.state.hiddenLabel} />;
    }
  }

  return connect(mapStateToProps)(WithHiddenLabel);
};

export default withHiddenLabel;
